import React, { useCallback, useState } from 'react'
import { Form, Input, Select, Button, Space, Col, Row } from 'antd'
import * as Icons from '@ant-design/icons'
import styled from 'styled-components'
import _ from 'lodash'
import { Breadcrumb } from 'admin/lib/Breadcrumbs'
import { Layout } from 'admin/lib/Layouts'
import { saveJsonFile } from 'admin/lib/files'
import { getMatcher, useGlobalShortcut } from 'admin/lib/shortcuts'
import { ShortcutsModalButton } from 'admin/lib/ShortcutsModalButton'
import { useApiQuery } from 'admin/lib/Api'
import { Image } from './Image'
import { Designer } from './Designer'
import { GetRegionType } from './RegionType'
import { useDimensions } from './useDimensions'
import { OpenTicketButton } from './OpenTicketButton'
import { OpenJsonButton } from './OpenJsonButton'
import { SmallScreenSpin } from 'admin/lib/ScreenSpin'

const supportedModules = [
  { label: 'Delivery', value: 'delivery' },
  { label: 'Waste', value: 'Waste' }
]

export const TemplateGui = () => {
  const [image, setImage] = useState(null)
  const dimensions = useDimensions()
  const [template, setTemplate] = useState({ name: '', module: 'delivery' })
  const [regions, setRegions] = useState([])
  const [structuredTicket, setStructuredTicket] = useState(null)

  const useImageFromFile = image => {
    const { loaded, data } = useApiQuery(
      useCallback(
        api => {
          console.log('calling api with file ' + JSON.stringify(image))
          return api.ocrRotate(image)
        },
        [image]
      )
    )

    return [loaded, data]
  }

  const handleLoadJson = useCallback(data => {
    setTemplate({
      name: data.Name,
      module: data.Module
    })
    setRegions(
      data.Regions.map(region => ({
        field: region.Field,
        value: region.Value,
        x: region.Shape.X,
        y: region.Shape.Y,
        width: region.Shape.Width,
        height: region.Shape.Height,
        regionType: GetRegionType(region.RegionType, region.Field, region.Value)
      }))
    )
  }, [])

  const handleSaveJson = useCallback(() => {
    const width = structuredTicket ? structuredTicket.width : dimensions.width
    const height = structuredTicket
      ? structuredTicket.height
      : dimensions.height

    const fileName = template.name
      .toLowerCase()
      .replace(/\s/, '_')
      .replace(/\W/, '')

    const jsonData = {
      Name: template.name,
      Module: template.module,
      Dimensions: [width, height],
      Regions: regions.map(region => {
        const regionObject = {
          Shape: {
            X: region.x,
            Y: region.y,
            Width: region.width,
            Height: region.height
          },
          Loc: [
            region.x,
            region.y,
            region.x + region.width,
            region.y,
            region.x + region.width,
            region.y + region.height,
            region.x,
            region.y + region.height
          ].map(num => _.round(num, 4)),
          LocAbsolute: [
            width * region.x,
            height * region.y,
            width * (region.x + region.width),
            height * region.y,
            width * (region.x + region.width),
            height * (region.y + region.height),
            width * region.x,
            height * (region.y + region.height)
          ].map(num => _.round(num, 1)),
          RegionType: GetRegionType(
            region.regionType,
            region.field,
            region.value
          )
        }
        if (region.field && region.field !== '') {
          regionObject.Field = region.field
        }
        if (region.value && region.value !== '') {
          regionObject.Value = region.value
        }
        return regionObject
      })
    }

    saveJsonFile(fileName, jsonData)
  }, [
    dimensions.height,
    dimensions.width,
    regions,
    structuredTicket,
    template.module,
    template.name
  ])

  useGlobalShortcut(
    getMatcher({ group: 'File', name: 'Save JSON' }, 'S', {
      ctrlOrCommand: true
    }),
    handleSaveJson
  )

  const [loaded, data] = useImageFromFile(image)
  if (data && structuredTicket != data.pages[0]) {
    setStructuredTicket(data.pages[0])
  }

  return (
    <Layout
      header={
        <>
          <Breadcrumb items={[{ title: 'Templates' }, { title: 'Edit' }]} />

          <Space>
            <OpenTicketButton onOpen={setImage} />

            <OpenJsonButton onOpen={handleLoadJson} />

            <Button
              icon={<Icons.DownloadOutlined />}
              type="primary"
              onClick={handleSaveJson}
            >
              Save JSON
            </Button>

            <ShortcutsModalButton />
          </Space>
        </>
      }
    >
      <Col>
        <Row justify="center" style={{ padding: '1rem 0' }}>
          <Form layout="inline">
            <Form.Item label="Name">
              <Input
                value={template.name}
                onChange={e =>
                  setTemplate({ ...template, name: e.target.value })
                }
              />
            </Form.Item>

            <Form.Item label="Module">
              <Select
                value={template.module}
                onChange={value => setTemplate({ ...template, module: value })}
                options={supportedModules}
              />
            </Form.Item>
          </Form>
        </Row>

        <ImageContainer>
          {!loaded && image && <SmallScreenSpin />}
          {loaded && data && (
            <Image
              data={data}
              ref={dimensions.dimensionsRef}
              onLoad={dimensions.forceUpdate}
            />
          )}

          <Designer
            moduleName={template.module}
            height={dimensions.height}
            width={dimensions.width}
            regions={regions}
            onRegionsChanged={setRegions}
          />
        </ImageContainer>
      </Col>
    </Layout>
  )
}

const ImageContainer = styled.div`
  display: flex;
  flex: 1 1 auto;

  max-width: 60rem;

  position: relative;
`
